import dataHubManager, { DataHubOperationType, DataHubStorageOperations } from '../../managers/data/DataHubManager'
import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../api-services/ArmadoresServices'
import { IChange, RequestedChange } from '../api-services/ChangesServices'
import { IEmbarcacion, OperatedEmbarcacion } from '../api-services/EmbarcacionesServices'
import { IRequest, RequestObjectType, RequestStatus } from '../api-services/RequestsServices'
import { ITripulante, OperatedTripulante } from '../api-services/TripulantesServices'
import { IUser } from '../api-services/UsersServices'
import { normalizeSearch, rutify, normalizeRut } from '../apiUtils'
import { esCompleto } from '../utils/usuarioCompletitud'
import { changesHubName } from './ChangesHub'
import dataHubServices, { defaultRefreshTime, StorageService } from './DataHubServices'

export enum TripulantesHubPipelines {
  TRIPULANTES_WITH_REQUESTS = 'tripulantes_with_requests', // Pipeline para mostrar los tripulantes con su solicitud más reciente (si existe)
  TRIPULANTES_WITH_CHANGES = 'tripulantes_with_changes', // Pipeline para mostrar los tripulantes con sus cambios relacionados
  TRIPULANTES_WITH_ASOCIATIONS = 'tripulantes_with_asociations', // Pipeline para mostrar los tripulantes con sus solicitudes de asociación más recientes (si existen)
  TRIPULANTES_WITH_STATUS = 'tripulantes_with_status', // Pipeline para mostrar los tripulantes con su estado de enrolamiento (enrolado, pre-enrolado, no enrolado)
  TRIPULANTES_WITH_ARMADOR = 'tripulantes_with_armador', // Pipeline para mostrar los tripulantes con sus armadores relacionados
  TRIPULANTES_WITH_USER = 'tripulantes_with_user', // Pipeline para mostrar los tripulantes con sus datos de usuario relacionados
  TRIPULANTES_FULL = 'tripulantes_full', // Pipeline completo para mostrar toda la información relacionada a un tripulante (solicitud, cambios, asociación, cobros, etc)
}

export const tripulantesHubName = 'tripulantesHub'

export class TripulantesHub implements StorageService {
  bucketData: any[] = []

  storageOperations: DataHubStorageOperations<IEmbarcacion> = {
    getData: () => [],
    markDirty: () => {},
    markStaled: () => {},
    forceRefresh: async () => {},
  }
  pipelinesOperations: Map<TripulantesHubPipelines, DataHubStorageOperations<any>> = new Map()

  constructor() {}

  public async startup() {
    dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, (data) => {
      this.bucketData = data
      this.storageOperations.forceRefresh()
    })
    this.storageOperations = dataHubManager.registerStorage(
      tripulantesHubName,
      async () => {
        return this.bucketData
      },
      {
        invalidationStrategy: 'hybrid',
        checkTime: defaultRefreshTime,
        lazyLoad: true,
        pipelines: [
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_REQUESTS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: ITripulante[]) => {
                  const dataWithRequests = this.addRequestToTripulantes(data)
                  return dataWithRequests
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_CHANGES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addChanges',
                execute: (data: OperatedTripulante[]) => {
                  const dataWithChanges = this.addChangesToTripulantes(data)
                  return dataWithChanges
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_ASOCIATIONS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addAsociations',
                execute: (data: OperatedTripulante[]) => {
                  const dataWithAsociations = this.addAsociationsToTripulantes(data)
                  return dataWithAsociations
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_STATUS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addStatus',
                execute: (data: OperatedTripulante[]) => {
                  const dataWithStatus = this.addStatusToTripulantes(data)
                  return dataWithStatus
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_ARMADOR,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addArmador',
                execute: (data: OperatedTripulante[]) => {
                  const dataWithArmador = this.addArmadorToTripulantes(data)
                  return dataWithArmador
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_WITH_USER,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addUser',
                execute: (data: OperatedTripulante[]) => {
                  const dataWithUser = this.addUserToTripulantes(data)
                  return dataWithUser
                },
              },
            ],
          },
          {
            name: TripulantesHubPipelines.TRIPULANTES_FULL,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'setFullData',
                execute: (data: OperatedTripulante[]) => {
                  const fullData = this.setFullDataToTripulantes(data)
                  return fullData
                },
              },
            ],
          },
        ],
      },
    )

    dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, () => {
      this.storageOperations.forceRefresh()
    })

    dataHubServices.subscribeToHub(changesHubName, {
      subscriberID: 'embarcacionesHub_changes_subscription',
      pipeline: null,
      callback: () => {
        console.log('Changes updated, refreshing embarcaciones data...')
        this.storageOperations.forceRefresh()
      },
    })
  }

  private addRequestToTripulantes(tripulantes: ITripulante[]): Array<OperatedTripulante> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const operatedTripulantes: Array<OperatedTripulante> = tripulantes.map((tripulante) => {
      const requestData = requestsList
        .filter((req) => req.tipo_objeto === RequestObjectType.Tripulante)
        .filter((req) => req.id_objeto.toString() === tripulante.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      return {
        ...tripulante,
        requestData: requestData,
        changesData: [],
        asociationData: undefined,
        asociationsData: [],
        armadorData: undefined,
        userData: undefined,
      }
    })
    return operatedTripulantes
  }

  private addChangesToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    const fullChangesHub = dataHubServices.getHub(changesHubName)
    const fullChangesList: Array<RequestedChange> = fullChangesHub ? fullChangesHub.storageOperations.getData() : []
    const operatedTripulantes: Array<OperatedTripulante> = tripulantes.map((item) => {
      const changesData = fullChangesList.filter((ch) => ch.id_objeto.toString() === item.id.toString())
      return {
        ...item,
        changesData: changesData,
      }
    })
    return operatedTripulantes
  }

  private addAsociationsToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    const fullRequestList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const asociationsRequests = fullRequestList.filter(
      (req) => req.tipo_objeto === RequestObjectType.Asociacion_tripulante,
    )
    const operatedTripulantes: Array<OperatedTripulante> = tripulantes.map((item) => {
      const asociationsData = asociationsRequests.filter((req) => req.id_objeto.toString() === item.id.toString())
      return {
        ...item,
        asociationsData: asociationsData,
      }
    })
    return operatedTripulantes
  }

  private addStatusToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    // La decisión final de estado se hace en setFullDataToTripulantes (último pipeline step),
    // cuando userData y armadorData ya están populados. Aquí sólo se pasa adelante.
    return tripulantes
  }

  private addArmadorToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    const armadoresList = dataStorageManager.getCurrentBucketData<IArmador>(BucketDefaultIDs.ARMADORES) || []
    const operatedTripulantes: Array<OperatedTripulante> = tripulantes.map((item) => {
      const armadorData = armadoresList.find((a) => rutify(a.rut_representante) === rutify(item.rut))
      return {
        ...item,
        armadorData: armadorData,
      }
    })
    return operatedTripulantes
  }

  private addUserToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    const userList = dataStorageManager.getCurrentBucketData<IUser>(BucketDefaultIDs.USERS) || []
    const operatedTripulantes: Array<OperatedTripulante> = tripulantes.map((item) => {
      const userData = userList.find((u) => rutify(u.rut) === rutify(item.rut))
      return {
        ...item,
        userData: userData,
      }
    })
    return operatedTripulantes
  }

  private setFullDataToTripulantes(tripulantes: OperatedTripulante[]): Array<OperatedTripulante> {
    // Último paso: re-leemos los buckets frescos y decidimos el estado igual que la vista armador
    // (EnrollmentTripulantesPage.vue modTunnel). No depende del orden de pipelines anteriores.
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const userList = dataStorageManager.getCurrentBucketData<IUser>(BucketDefaultIDs.USERS) || []

    const getCanceladosMap = (): Record<string, { motivo: string; fecha?: string }> => {
      try {
        const raw = localStorage.getItem('tripulantes_cancelados_definitivos')
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          const m: Record<string, { motivo: string }> = {}
          parsed.forEach((x: any) => {
            if (typeof x === 'string') m[normalizeRut(x)] = { motivo: 'Cancelación definitiva' }
            else if (x?.rut) m[normalizeRut(x.rut)] = { motivo: x.motivo || 'Cancelación definitiva' }
          })
          return m
        }
        if (typeof parsed === 'object' && parsed !== null) {
          const m: Record<string, { motivo: string; fecha?: string }> = {}
          Object.keys(parsed).forEach((k) => {
            m[normalizeRut(k)] = parsed[k]
          })
          return m
        }
        return {}
      } catch {
        return {}
      }
    }
    const canceladosMap = getCanceladosMap()

    return tripulantes.map((item) => {
      // 0. Identificar si cuenta con cancelación definitiva
      const normRut = normalizeRut(item.rut)
      const canceladoStorage = canceladosMap[normRut]
      const canceladoByRequest = requestsList.some(
        (req) =>
          req.tipo_objeto === RequestObjectType.Tripulante &&
          req.id_objeto.toString() === item.id.toString() &&
          req.razon_rechazo?.toUpperCase().includes('CANCELACI'),
      )
      const isCancelled = Boolean(
        item.cancelado_definitivo || canceladoByRequest || (item.activo === false && canceladoStorage),
      )
      const motivoCancel =
        canceladoStorage?.motivo ||
        (canceladoByRequest
          ? requestsList.find(
              (req) =>
                req.tipo_objeto === RequestObjectType.Tripulante &&
                req.id_objeto.toString() === item.id.toString() &&
                req.razon_rechazo?.toUpperCase().includes('CANCELACI'),
            )?.razon_rechazo
          : item.motivo_cancelacion) ||
        'Cancelación definitiva por resolución de EPV'

      if (isCancelled) {
        item.cancelado_definitivo = true
        item.activo = false
        item.motivo_cancelacion = motivoCancel
      }

      // 1. Re-seleccionar la solicitud más reciente de tipo Tripulante para este id
      const request = requestsList
        .filter((req) => req.tipo_objeto === RequestObjectType.Tripulante)
        .filter((req) => req.id_objeto.toString() === item.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]

      // 2. Mergear imagen_perfil / carnet_adjunto desde el usuario (fuente de verdad si existen y son válidos)
      const userData = userList.find((u) => rutify(u.rut) === rutify(item.rut))
      if (userData) {
        if (userData.id_gestor_fotografia && userData.id_gestor_fotografia > 0) {
          item.imagen_perfil = userData.id_gestor_fotografia
        }
        if (userData.id_gestor_ci && userData.id_gestor_ci > 0) {
          item.carnet_adjunto = userData.id_gestor_ci
        }
      }

      // 3. Usuario deshabilitado (vigente=0): tratar como incompleto sin request visible
      if (userData && userData.vigente === 0) {
        return {
          ...item,
          activo: isCancelled ? false : item.activo,
          cancelado_definitivo: isCancelled,
          motivo_cancelacion: isCancelled ? motivoCancel : item.motivo_cancelacion,
          requestData: undefined,
          changesData: [],
          userData: userData,
        }
      }

      // 4. Validar completitud con la fuente de verdad única (ver usuarioCompletitud.ts), idéntica a la
      //    usada por ArmadoresHub. La foto/CI se toman del usuario si existen, de lo contrario del tripulante;
      //    la matrícula solo se exige a los patrones. Si está incompleto, se invalida la request para mostrar "Incompleto".
      const idFoto =
        userData?.id_gestor_fotografia && userData.id_gestor_fotografia > 0
          ? userData.id_gestor_fotografia
          : item.imagen_perfil

      const idCarnet = userData?.id_gestor_ci && userData.id_gestor_ci > 0 ? userData.id_gestor_ci : item.carnet_adjunto

      const yaAprobado =
        request?.estado === RequestStatus.APPROVED ||
        requestsList.some(
          (req) =>
            req.tipo_objeto === RequestObjectType.Tripulante &&
            req.id_objeto.toString() === item.id.toString() &&
            req.estado === RequestStatus.APPROVED,
        )

      const validDocs = esCompleto({
        tipoPersona: 'natural',
        idFotografia: idFoto,
        idCarnet: idCarnet,
        esPatron: item.cargo === 'patron',
        idMatricula: item.id_matricula,
        yaAprobado: yaAprobado,
      })

      // 5. Solicitud siempre visible: si la persona está completa pero nunca se le generó la
      //    solicitud de tripulante (caso típico: un patrón que reutiliza el usuario del armador),
      //    se sintetiza una solicitud VIRTUAL en estado PENDING para que el administrador la vea
      //    igual. No existe en el backend: al aprobarla/rechazarla se materializa (insert) desde
      //    RequestShower.saveChanges. Si está incompleta, se deja sin solicitud para mostrar el
      //    estado "Incompleto" con sus razones (la completitud gobierna a la solicitud).
      const requestData = validDocs ? request ?? this.buildVirtualTripulanteRequest(item) : undefined

      return {
        ...item,
        activo: isCancelled ? false : item.activo,
        cancelado_definitivo: isCancelled,
        motivo_cancelacion: isCancelled ? motivoCancel : item.motivo_cancelacion,
        requestData: requestData,
        changesData: validDocs ? item.changesData : [],
        userData: userData,
        // Precomputamos la cadena de búsqueda una sola vez (al cargar/refrescar el hub) sobre los
        // mismos campos que ofrecen los filtros por columna. El filtro "Todos los campos" la consume
        // tal cual y evita N normalizaciones (NFD + replace + toLowerCase) por ítem en cada tecleo.
        _search: normalizeSearch([item.nombre_completo, item.rut, item.telefono, item.email, item.cargo].join(' ')),
      }
    })
  }

  // Construye una solicitud de tripulante VIRTUAL (no persistida) en estado PENDING. Espeja el
  // solicitante y la fecha de solicitud de la solicitud de Armador del mismo RUT cuando existe,
  // para que el panel muestre datos coherentes; el estado se deja SIEMPRE PENDING porque la
  // decisión de aprobar/rechazar es del administrador (y al tomarla se materializa en el backend).
  private buildVirtualTripulanteRequest(item: OperatedTripulante): IRequest {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const armadorRequest = item.armadorData
      ? requestsList
          .filter(
            (r) =>
              r.tipo_objeto === RequestObjectType.Armador && r.id_objeto.toString() === item.armadorData!.id.toString(),
          )
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      : undefined

    return {
      id: 0,
      creador: armadorRequest ? armadorRequest.creador : 0,
      revisor: null,
      estado: RequestStatus.PENDING,
      fecha_solicitud: armadorRequest ? armadorRequest.fecha_solicitud : new Date().toISOString(),
      fecha_respuesta: null,
      fecha_caducidad: new Date(new Date().getFullYear() + 1, 11, 31).toISOString(),
      tipo_objeto: RequestObjectType.Tripulante,
      id_objeto: item.id.toString(),
      tipo_objeto_2: null,
      id_objeto_2: null,
      razon_rechazo: '',
      esVirtual: true,
    }
  }
}

/*
  useComplexBucketList<OperatedTripulante, ITripulante>(BucketDefaultIDs.TRIPULANTES, (item) => {
    let request: IRequest | undefined = requestsList.value
      .filter((req) => req.tipo_objeto === RequestObjectType.Tripulante)
      .filter((req) => req.id_objeto.toString() === item.id.toString())
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
    let changesData = fullChangesList.value.filter((ch) => ch.id_objeto.toString() === item.id.toString())
    const asociationsData = asociationsRequests.value
      .filter((req) =>
        req.id_objeto.toString().includes('-')
          ? req.id_objeto.toString().split('-')[0].toString() === item.id.toString()
          : req.id_objeto.toString() === item.id.toString(),
      )
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())

    const armadorData = armadoresList.value.find((a) => rutify(a.rut_representante) === rutify(item.rut))
    const armadorRequest = armadoresRequests.value
      .filter((req) => req.id_objeto.toString() === armadorData?.id.toString())
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
    const userData = userList.value.find((u) => rutify(u.rut) === rutify(item.rut))
    if (armadorData && userData) {
      if (userData.id_gestor_fotografia && Number(userData.id_gestor_fotografia) > 0) {
        item.imagen_perfil = Number(userData.id_gestor_fotografia)
      }
      if (userData.id_gestor_ci && Number(userData.id_gestor_ci) > 0) {
        item.carnet_adjunto = Number(userData.id_gestor_ci)
      }
    }
    const armadorActiveChanges = changesData.filter((ch) => ch.modulo === ChangeModule.Armador && ch.estado === 0)

    let status = request ? request.estado : RequestStatus.INCOMPLETE
    const imagenPerfilValid = item.imagen_perfil !== 0 && item.imagen_perfil != null
    const matriculaValid = item.id_matricula !== 0 && item.id_matricula != null
    const carnetValid = item.carnet_adjunto !== 0 && item.carnet_adjunto != null

    const validDocs = matriculaValid && carnetValid && imagenPerfilValid
    if (!validDocs) {
      status = RequestStatus.INCOMPLETE
      request = undefined
      changesData = []
    }
    return {
      ...item,
      requestData: armadorRequest ? armadorRequest : request,
      changesData: changesData,
      asociationsData: asociationsData,
      armadorData: armadorData,
      userData: userData,
    }
  })


*/
